export class ServiceManager {
  constructor(devtools) {
    this.devtools = devtools;
    this.services = new Map();
    this.integrations = new Map();
    this.webhooks = new Map();
  }

  // Register external service integrations
  registerService(name, config) {
    const service = {
      name,
      config: {
        apiKey: null,
        endpoint: null,
        headers: {},
        enabled: false,
        ...config
      },
      methods: new Map(),
      lastSync: null,
      status: 'disconnected'
    };

    this.services.set(name, service);
    this.devtools.apiGlue.triggerHook('service:registered', { name, service });
    
    return service;
  }

  // Add method to service (like sendError, getMetrics, etc.)
  addServiceMethod(serviceName, methodName, handler) {
    const service = this.services.get(serviceName);
    if (!service) {
      throw new Error(`Service ${serviceName} not found`);
    }

    service.methods.set(methodName, handler);
  }

  // Connect to external service
  async connectService(serviceName, credentials = {}) {
    const service = this.services.get(serviceName);
    if (!service) {
      throw new Error(`Service ${serviceName} not found`);
    }

    try {
      service.config = { ...service.config, ...credentials };
      
      // Test connection
      const testResult = await this.testConnection(service);
      if (testResult.success) {
        service.status = 'connected';
        service.lastSync = Date.now();
        
        this.devtools.apiGlue.triggerHook('service:connected', { 
          name: serviceName, 
          service 
        });
        
        return { success: true, message: 'Connected successfully' };
      } else {
        service.status = 'error';
        return { success: false, message: testResult.error };
      }
    } catch (error) {
      service.status = 'error';
      return { success: false, message: error.message };
    }
  }

  async testConnection(service) {
    try {
      const response = await fetch(service.config.endpoint + '/health', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${service.config.apiKey}`,
          'Content-Type': 'application/json',
          ...service.config.headers
        }
      });

      if (response.ok) {
        return { success: true };
      } else {
        return { success: false, error: `HTTP ${response.status}` };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Send error to external service
  async sendErrorToService(serviceName, errorData) {
    const service = this.services.get(serviceName);
    if (!service || service.status !== 'connected') {
      return { success: false, message: 'Service not connected' };
    }

    try {
      const payload = this.formatErrorForService(errorData, service);
      
      const response = await fetch(service.config.endpoint + '/errors', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${service.config.apiKey}`,
          'Content-Type': 'application/json',
          ...service.config.headers
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        return { success: true, data: result };
      } else {
        return { success: false, message: `HTTP ${response.status}` };
      }
    } catch (error) {
      return { success: false, message: error.message };
    }
  }

  formatErrorForService(errorData, service) {
    // Format error data based on service requirements
    const basePayload = {
      timestamp: errorData.timestamp,
      message: errorData.message,
      type: errorData.type,
      source: errorData.source,
      line: errorData.lineno,
      column: errorData.colno,
      stack: errorData.stackTrace,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Service-specific formatting
    switch (service.name) {
      case 'sentry':
        return {
          ...basePayload,
          level: this.mapSeverityToSentry(errorData.analysis?.severity?.level),
          tags: {
            environment: 'development',
            browser: this.getBrowserInfo()
          }
        };
      
      case 'bugsnag':
        return {
          ...basePayload,
          severity: errorData.analysis?.severity?.level || 'error',
          context: errorData.source,
          metaData: {
            analysis: errorData.analysis
          }
        };
      
      case 'rollbar':
        return {
          access_token: service.config.apiKey,
          data: {
            environment: 'development',
            body: {
              trace: {
                frames: this.formatStackTrace(errorData.stackTrace),
                exception: {
                  class: errorData.type,
                  message: errorData.message
                }
              }
            }
          }
        };
      
      default:
        return basePayload;
    }
  }

  mapSeverityToSentry(level) {
    const mapping = {
      'critical': 'fatal',
      'high': 'error',
      'medium': 'warning',
      'low': 'info'
    };
    return mapping[level] || 'error';
  }

  getBrowserInfo() {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  formatStackTrace(stackTrace) {
    return stackTrace.map(line => {
      const match = line.match(/at\s+(.+)\s+\((.+):(\d+):(\d+)\)/);
      if (match) {
        const [, method, filename, lineno, colno] = match;
        return {
          filename,
          method,
          lineno: parseInt(lineno),
          colno: parseInt(colno)
        };
      }
      return { filename: 'unknown', method: line, lineno: 0, colno: 0 };
    });
  }

  // Webhook system for real-time notifications
  registerWebhook(name, url, events = []) {
    this.webhooks.set(name, {
      url,
      events,
      enabled: true,
      lastTriggered: null
    });
  }

  async triggerWebhook(eventType, data) {
    for (const [name, webhook] of this.webhooks) {
      if (webhook.enabled && (webhook.events.length === 0 || webhook.events.includes(eventType))) {
        try {
          await fetch(webhook.url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              event: eventType,
              timestamp: Date.now(),
              data
            })
          });
          webhook.lastTriggered = Date.now();
        } catch (error) {
          console.warn(`Webhook ${name} failed:`, error);
        }
      }
    }
  }

  // Pre-configured service templates
  getServiceTemplates() {
    return {
      sentry: {
        name: 'Sentry',
        endpoint: 'https://sentry.io/api/0',
        fields: [
          { name: 'apiKey', label: 'Auth Token', type: 'password', required: true },
          { name: 'projectId', label: 'Project ID', type: 'text', required: true }
        ],
        description: 'Real-time error tracking and performance monitoring'
      },
      
      bugsnag: {
        name: 'Bugsnag',
        endpoint: 'https://notify.bugsnag.com',
        fields: [
          { name: 'apiKey', label: 'API Key', type: 'password', required: true }
        ],
        description: 'Application stability monitoring'
      },
      
      rollbar: {
        name: 'Rollbar',
        endpoint: 'https://api.rollbar.com/api/1',
        fields: [
          { name: 'apiKey', label: 'Access Token', type: 'password', required: true }
        ],
        description: 'Real-time error alerting & debugging'
      },
      
      slack: {
        name: 'Slack',
        endpoint: 'https://hooks.slack.com/services',
        fields: [
          { name: 'webhookUrl', label: 'Webhook URL', type: 'url', required: true },
          { name: 'channel', label: 'Channel', type: 'text', required: false }
        ],
        description: 'Send error notifications to Slack'
      },
      
      discord: {
        name: 'Discord',
        endpoint: 'https://discord.com/api/webhooks',
        fields: [
          { name: 'webhookUrl', label: 'Webhook URL', type: 'url', required: true }
        ],
        description: 'Send error notifications to Discord'
      }
    };
  }

  // Get service status dashboard data
  getServiceStatus() {
    const status = {};
    
    for (const [name, service] of this.services) {
      status[name] = {
        connected: service.status === 'connected',
        lastSync: service.lastSync,
        errorCount: 0, // Would track actual sent errors
        enabled: service.config.enabled
      };
    }
    
    return status;
  }
}